const isBlank = (value: string) => value.trim().length === 0;

export const required = (value: string, errorKey = 'common.validation.required') => {
  return isBlank(value) ? errorKey : undefined;
};

export const isEmail = (value: string, errorKey = 'common.validation.invalid_email') => {
  if (isBlank(value)) {
    return undefined;
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(value) ? undefined : errorKey;
};

export const minLength = (value: string, minimum: number, errorKey = 'common.validation.min_length') => {
  return value.length >= minimum ? undefined : errorKey;
};

export const matches = (value: string, expectedValue: string, errorKey: string) => {
  return value === expectedValue ? undefined : errorKey;
};

export const isPositiveNumber = (value: string | number, errorKey = 'common.validation.positive_number') => {
  const numericValue = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(numericValue) && numericValue > 0
    ? undefined
    : errorKey;
};

export const fileType = (
  file: Pick<File, 'type'> | null | undefined,
  allowedTypes: string[],
  errorKey = 'common.validation.invalid_file_type',
) => {
  if (!file) {
    return 'common.validation.required';
  }

  return allowedTypes.includes(file.type) ? undefined : errorKey;
};

export const fileSize = (
  file: Pick<File, 'size'> | null | undefined,
  maxBytes: number,
  errorKey = 'common.validation.file_too_large',
) => {
  if (!file) {
    return 'common.validation.required';
  }

  return file.size <= maxBytes ? undefined : errorKey;
};

export const atLeastOneSelected = (selected: Record<string, boolean>, errorKey = 'common.validation.required') => {
  return Object.values(selected).some(Boolean) ? undefined : errorKey;
};

export type PasswordComplexity = {
  minLength: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
};

export const getPasswordComplexity = (value: string): PasswordComplexity => {
  return {
    minLength: value.length >= 8,
    uppercase: /[A-Z]/.test(value),
    lowercase: /[a-z]/.test(value),
    number: /\d/.test(value),
  };
};

export const passwordComplexity = (value: string, errorKey = 'common.validation.password_complexity') => {
  const complexity = getPasswordComplexity(value);
  const isValid = complexity.minLength && complexity.uppercase && complexity.lowercase && complexity.number;
  return isValid ? undefined : errorKey;
};
